import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import {
  Eye,
  EyeOff,
  UserPlus,
  ArrowRight,
  ArrowLeft,
  Check,
  Mail,
  Phone,
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { RegisterData } from '../../utils/types';
import OnboardingSource from '../../components/auth/OnboardingSource';
import VerificationModal from '../../components/auth/VerificationModal';
import {
  VALIDATION_MESSAGES,
  REGEX_PATTERNS,
  VALIDATORS,
  preventInvalidNameInput,
} from '../../utils/validationMessages';
import { PUBLIC_ROUTES } from '../../constants/routeConstants';
import DatePicker from '../../components/common/DatePicker';

interface FormData {
  source: string;
  referralCode: string;
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  dob: string;
  emailVerified: boolean;
  phoneVerified: boolean;
}

const steps = [
  {
    title: 'Source of Onboarding',
    description: 'Select how you heard about us',
  },
  {
    title: 'Registration',
    description: 'Create your account',
  },
];

const SignUp = () => {
  const navigate = useNavigate();
  const { register: registerUser, loading } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showEmailOTP, setShowEmailOTP] = useState(false);
  const [showPhoneOTP, setShowPhoneOTP] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredPhone, setRegisteredPhone] = useState('');
  const [isEmailVerificationLoading, setIsEmailVerificationLoading] =
    useState(false);
  const [isPhoneVerificationLoading, setIsPhoneVerificationLoading] =
    useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      source: '',
      referralCode: '',
      fullName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      dob: '',
      emailVerified: false,
      phoneVerified: false,
    },
    mode: 'onChange',
  });

  const [searchParams] = useSearchParams();
  const isReferralLinkRegistration =
    searchParams.get('source') === 'ib_referral' &&
    Boolean(searchParams.get('referralCode'));

  useEffect(() => {
    const source = searchParams.get('source');
    const referralCode = searchParams.get('referralCode');

    if (source) {
      setValue('source', source);
    }

    if (referralCode) {
      setValue('referralCode', referralCode);
    }
  }, [searchParams, setValue]);

  const handleSendEmailOTP = async () => {
    const data = watch();
    if (
      !data.email ||
      !data.fullName ||
      !data.password ||
      !data.dob ||
      !phone
    ) {
      toast.error('Please fill all required fields first');
      return;
    }

    setIsEmailVerificationLoading(true);

    try {
      const registrationPayload = {
        source: data.source || '',
        referralCode: data.referralCode,
        fullName: data.fullName || '',
        email: data.email || '',
        phone: phone || '',
        password: data.password || '',
        dob: data.dob || '',
      } as RegisterData;

      const result = await registerUser(registrationPayload);

      if (result.success) {
        setRegisteredEmail(data.email);
        setRegisteredPhone(phone);
        try {
          const otpResponse = await authAPI.sendEmailOTP(data.email);
          if (otpResponse.success) {
            const message =
              otpResponse.toastMessage ||
              otpResponse.message ||
              'OTP sent to your email successfully';
            toast.success(message);
            setShowEmailOTP(true);
          } else {
            const errorMessage =
              otpResponse.message || otpResponse.error || 'Failed to send OTP';
            toast.error(errorMessage);
          }
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Failed to send OTP';
          toast.error(errorMessage);
        }
      } else {
        const errorMessage =
          result.message || result.error || 'Registration failed';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Registration failed';
      toast.error(errorMessage);
    } finally {
      setIsEmailVerificationLoading(false);
    }
  };

  const handleEmailVerificationSuccess = () => {
    setIsEmailVerificationLoading(false);
    setEmailVerified(true);
    setShowEmailOTP(false);
  };

  const handleEmailVerificationClose = () => {
    setIsEmailVerificationLoading(false);
    setShowEmailOTP(false);
  };

  const handleSendPhoneOTP = async () => {
    if (!registeredPhone) return;
    setIsPhoneVerificationLoading(true);

    try {
      const otpResponse = await authAPI.sendPhoneOTP(registeredPhone);
      if (otpResponse.success) {
        const message =
          otpResponse.message ||
          otpResponse.toastMessage ||
          'OTP sent to your phone successfully';
        toast.success(message);
        setShowPhoneOTP(true);
      } else {
        const errorMessage =
          otpResponse.message || otpResponse.error || 'Failed to send OTP';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send OTP';
      toast.error(errorMessage);
    } finally {
      setIsPhoneVerificationLoading(false);
    }
  };

  const handlePhoneVerificationSuccess = () => {
    setIsPhoneVerificationLoading(false);
    setPhoneVerified(true);
    setShowPhoneOTP(false);
  };

  const handlePhoneVerificationClose = () => {
    setIsPhoneVerificationLoading(false);
    setShowPhoneOTP(false);
  };

  const onSubmitStep = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (emailVerified && phoneVerified) {
      navigate(`${PUBLIC_ROUTES.LOGIN}`);
    }
  };

  const onValidationError = (errors: any) => {
    const firstError = Object.values(errors)[0] as any;
    if (firstError?.message) {
      toast.error(firstError.message);
    } else {
      toast.error('Please fill all required fields correctly');
    }
  };

  const password = watch('password');
  const source = watch('source');

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <OnboardingSource
            control={control}
            errors={errors}
            source={source}
            fieldsDisabled={isReferralLinkRegistration}
          />
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground">
                Registration
              </h3>
              <p className="text-sm text-muted-foreground">
                Please provide your personal details
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <h4 className="text-md font-medium">Personal Information</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      placeholder="Enter your full name as per Aadhar & PAN"
                      className="w-full"
                      onKeyDown={preventInvalidNameInput}
                      {...register('fullName', {
                        required: VALIDATION_MESSAGES.fullName.required,
                        minLength: {
                          value: 2,
                          message: VALIDATION_MESSAGES.fullName.minLength,
                        },
                        maxLength: {
                          value: 50,
                          message: VALIDATION_MESSAGES.fullName.maxLength,
                        },
                        pattern: {
                          value: REGEX_PATTERNS.name,
                          message: VALIDATION_MESSAGES.fullName.pattern,
                        },
                      })}
                    />
                    {errors.fullName && (
                      <p className="text-sm text-destructive">
                        {errors.fullName.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 w-full">
                    <Label htmlFor="dob">Date of Birth</Label>
                    <div className="relative w-full">
                      <Controller
                        name="dob"
                        control={control}
                        rules={{
                          required: 'Date of birth is required',
                          validate: value => {
                            if (!value) return 'Date of birth is required';
                            if (typeof value !== 'string')
                              return 'Invalid date format';
                            const age =
                              new Date().getFullYear() -
                              new Date(value).getFullYear();
                            if (age < 18)
                              return 'You must be at least 18 years old';
                            if (age > 100)
                              return 'Please enter a valid date of birth';
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <DatePicker
                            id="dob"
                            className="w-full"
                            value={field.value}
                            onChange={field.onChange}
                            hasError={!!errors.dob}
                          />
                        )}
                      />
                      {/* <Input
                        id="dob"
                        type="date"
                        {...register('dob', {
                          required: 'Date of birth is required',
                          validate: value => {
                            if (!value) return 'Date of birth is required';
                            if (typeof value !== 'string')
                              return 'Invalid date format';
                            const age =
                              new Date().getFullYear() -
                              new Date(value).getFullYear();
                            if (age < 18)
                              return 'You must be at least 18 years old';
                            if (age > 100)
                              return 'Please enter a valid date of birth';
                            return true;
                          },
                        })}
                        className={`w-full ${errors.dob ? 'border-red-500' : ''} cursor-pointer pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        onClick={e => {
                          const input = e.currentTarget;
                          if (input.showPicker) {
                            input.showPicker();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-10 w-10 hover:bg-transparent pointer-events-none"
                      >
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      </Button> */}
                    </div>
                    {errors.dob && (
                      <p className="text-sm text-destructive">
                        {errors.dob.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email">Email Address</Label>
                      {!emailVerified ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleSendEmailOTP}
                          disabled={isEmailVerificationLoading}
                          title="Click to register and verify email"
                        >
                          {isEmailVerificationLoading ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-1"></div>
                          ) : (
                            <Mail className="h-3 w-3 mr-1" />
                          )}
                          Verify Email
                        </Button>
                      ) : null}
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Email address"
                        className="w-full pl-10"
                        disabled={emailVerified}
                        {...register('email', {
                          required: VALIDATION_MESSAGES.email.required,
                          pattern: {
                            value: REGEX_PATTERNS.email,
                            message: VALIDATION_MESSAGES.email.invalid,
                          },
                          maxLength: {
                            value: 100,
                            message: VALIDATION_MESSAGES.email.maxLength,
                          },
                          validate: (value: string) => {
                            return (
                              VALIDATORS.email(value) ||
                              VALIDATION_MESSAGES.email.invalid
                            );
                          },
                        })}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-sm text-destructive">
                        {errors.email.message}
                      </p>
                    )}
                    {emailVerified && (
                      <p className="text-sm text-green-600 font-medium">
                        Email verified
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 w-full">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="phone">Phone Number</Label>
                      {!phoneVerified ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleSendPhoneOTP}
                          disabled={
                            isPhoneVerificationLoading || !emailVerified
                          }
                          title={
                            emailVerified
                              ? 'Verify Phone'
                              : 'Verify email first'
                          }
                        >
                          {isPhoneVerificationLoading ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-1"></div>
                          ) : (
                            <Phone className="h-3 w-3 mr-1" />
                          )}
                          Verify Phone
                        </Button>
                      ) : (
                        <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                          <Check className="h-3 w-3" /> Verified
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <div className="w-full">
                        <PhoneInput
                          country={'in'}
                          value={phone}
                          onChange={phone => {
                            setPhone(phone);
                            setValue('phone', phone);
                          }}
                          disabled={phoneVerified}
                          inputClass="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-16 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          buttonClass="absolute left-0 top-0 h-10 w-12 rounded-l-md border-r border-input bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          dropdownClass="bg-background border-border rounded-md shadow-lg mt-1"
                          searchClass="bg-background border-input rounded-md"
                          containerClass="w-full"
                          preferredCountries={['in', 'us', 'uk', 'ca', 'au']}
                          enableSearch={true}
                          placeholder="Enter phone number"
                          inputStyle={{
                            width: '100%',
                            height: '40px',
                            paddingLeft: '60px',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '6px',
                            backgroundColor: 'hsl(var(--background))',
                            color: 'hsl(var(--foreground))',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                          }}
                          buttonStyle={{
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '6px 0 0 6px',
                            backgroundColor: 'hsl(var(--background))',
                            color: 'hsl(var(--foreground))',
                          }}
                        />
                      </div>
                    </div>
                    <input
                      type="hidden"
                      {...register('phone', {
                        required: VALIDATION_MESSAGES.phone.required,
                        validate: () => {
                          if (!phone || phone.length < 10) {
                            return VALIDATION_MESSAGES.phone.minLength;
                          }
                          if (phone.length > 15) {
                            return VALIDATION_MESSAGES.phone.maxLength;
                          }
                          return true;
                        },
                      })}
                    />
                    {errors.phone && (
                      <p className="text-sm text-destructive">
                        {errors.phone.message}
                      </p>
                    )}
                    {phoneVerified && (
                      <p className="text-sm text-green-600 font-medium">
                        Phone verified
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create password"
                        className="w-full pr-10"
                        {...register('password', {
                          required: VALIDATION_MESSAGES.password.required,
                          minLength: {
                            value: 8,
                            message: VALIDATION_MESSAGES.password.minLength,
                          },
                          pattern: {
                            value: REGEX_PATTERNS.password.strong,
                            message: VALIDATION_MESSAGES.password.weak,
                          },
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-sm text-destructive">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm password"
                        className="w-full pr-10"
                        {...register('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: value =>
                            value === password || 'Passwords do not match',
                        })}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-sm text-destructive">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <LoaderOverlay
      isLoading={
        loading || isEmailVerificationLoading || isPhoneVerificationLoading
      }
      loadingText="Processing..."
    >
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-2xl">
          <CardHeader className="space-y-1">
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary/10">
              <UserPlus className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl text-center">
              Create Account
            </CardTitle>
            <CardDescription className="text-center">
              Sign up for your forex trading account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit(onSubmitStep, onValidationError)}
              className="space-y-6"
            >
              <div className="flex items-center justify-between mb-8">
                {steps.map((step, index) => (
                  <div key={step.title} className="flex items-center">
                    <div
                      className={`flex flex-col items-center ${
                        index <= currentStep
                          ? 'text-primary'
                          : 'text-muted-foreground'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                          index < currentStep
                            ? 'bg-primary border-primary text-primary-foreground'
                            : index === currentStep
                              ? 'border-primary text-primary'
                              : 'border-muted-foreground text-muted-foreground'
                        }`}
                      >
                        {index < currentStep ? (
                          <Check className="h-5 w-5" />
                        ) : (
                          <span className="text-sm font-medium">
                            {index + 1}
                          </span>
                        )}
                      </div>
                      <span className="text-xs mt-2 font-medium hidden sm:block">
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-2 ${
                          index < currentStep ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">
                  {steps[currentStep].title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {steps[currentStep].description}
                </p>
              </div>

              {renderStepContent()}
              <div className="flex justify-between pt-4">
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    disabled={currentStep === 0 || loading}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>

                  <Button
                    type="submit"
                    disabled={
                      currentStep === steps.length - 1 &&
                      (!emailVerified || !phoneVerified)
                    }
                  >
                    {currentStep === steps.length - 1 ? (
                      emailVerified && phoneVerified ? (
                        <>
                          Go to Login
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      ) : (
                        'Submit'
                      )
                    ) : (
                      <>
                        Next
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </>
              </div>

              <div className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link
                  to={PUBLIC_ROUTES.LOGIN}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Email OTP Modal */}
      <VerificationModal
        isOpen={showEmailOTP}
        type="email"
        value={registeredEmail}
        onSuccess={handleEmailVerificationSuccess}
        onClose={handleEmailVerificationClose}
        otpAlreadySent={true}
      />

      {/* Phone OTP Modal */}
      <VerificationModal
        isOpen={showPhoneOTP}
        type="phone"
        value={registeredPhone}
        onSuccess={handlePhoneVerificationSuccess}
        onClose={handlePhoneVerificationClose}
        otpAlreadySent={true}
      />
    </LoaderOverlay>
  );
};

export default SignUp;
